/**
 * DeleteAccountDialog — a danger-zone confirm pop-in for the irreversible,
 * cascading hard delete of an account. Because deleting an account also deletes
 * all of its projects, work items, milestones, RAID items and status
 * reports, the confirm is gated on typing the account's key (not a plain
 * window.confirm). Token system only; sonner for success / error.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { MUTATION_ERROR_TOAST_ID } from '@/lib/toastIds';
import { useDeleteAccount } from '@/api/account';
// Shared control kit — ProjectForm is the kit's source of truth (§2.16/§2.8).
import { fieldStyle, btnGhostStyle, btnDangerStyle } from '@/components/ProjectForm';

export function DeleteAccountDialog({ account, onClose, onDeleted }: {
  account: { id: string; name: string; key: string };
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [typed, setTyped] = useState('');
  const deleteMutation = useDeleteAccount();
  const armed = typed.trim() === account.key;

  function handleDelete() {
    if (!armed || deleteMutation.isPending) return;
    deleteMutation.mutate(account.id, {
      onSuccess: () => {
        toast.success(`Deleted "${account.name}" and all of its data.`);
        onDeleted();
      },
      onError: () => toast.error(`Couldn't delete "${account.name}". Please try again.`, { id: MUTATION_ERROR_TOAST_ID }),
    });
  }

  return (
    <div
      className="pop-in"
      role="alertdialog"
      aria-label={`Delete account ${account.name}`}
      style={{
        // Danger card (§2.21): severity tint layered over the card surface +
        // a 3px left accent border that follows the corner curve — no full
        // stroke, no shadow; the luminance step does the elevation.
        backgroundColor: 'var(--surface)',
        backgroundImage: 'linear-gradient(var(--health-red-bg), var(--health-red-bg))',
        borderLeft: '3px solid var(--health-red)',
        borderRadius: 'var(--radius-panel)', padding: '18px 20px', marginBottom: 16,
      }}
    >
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--health-red)', margin: '0 0 8px' }}>
        Delete account
      </h3>
      <p style={{ margin: '0 0 12px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--text)', lineHeight: 1.5 }}>
        This permanently deletes <strong>{account.name}</strong> and everything tied to it — all
        of its projects, work items, milestones, RAID items and status reports.{' '}
        <strong>This cannot be undone.</strong>
      </p>
      <label htmlFor="del-acct-key" style={{ display: 'block', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 6 }}>
        Type the account key{' '}
        <code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text)', background: 'var(--surface-2)', padding: '1px 8px', borderRadius: 'var(--radius-pill)' }}>{account.key}</code>{' '}
        to confirm.
      </label>
      <input
        id="del-acct-key"
        autoFocus
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleDelete(); }}
        placeholder={account.key}
        className="focus-ring"
        style={{ ...fieldStyle, maxWidth: 280, fontFamily: 'var(--font-mono)' }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} className="pressable focus-ring" style={btnGhostStyle}>
          Cancel
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={!armed || deleteMutation.isPending}
          className="pressable focus-ring"
          style={{
            ...btnDangerStyle,
            cursor: armed ? 'pointer' : 'not-allowed',
            opacity: !armed || deleteMutation.isPending ? 0.4 : 1,
          }}
        >
          {deleteMutation.isPending ? 'Deleting…' : 'Delete permanently'}
        </button>
      </div>
    </div>
  );
}
